import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSettings } from '../../../context/SettingsContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  LayoutDashboard, Wallet, TrendingUp, Coins, Briefcase,
  FileText, Bell, LogOut, ChevronDown, User,
  Menu, X, CreditCard, Banknote, ArrowRightLeft, MessageSquare,
  BarChart3, Shield, PieChart, Newspaper
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useThemeColors() {
  const { currentTemplate, branding } = useTheme();
  return { ...(currentTemplate?.colors || {}), ...(branding?.customColors || {}) };
}

/* Icon map for nav items */
const ICON_MAP = {
  'Tableau de bord': LayoutDashboard,
  'Accueil': LayoutDashboard,
  'Investissements': Briefcase,
  'Placement': Briefcase,
  'Mes Investissements': PieChart,
  'Comparer': BarChart3,
  'Simulation': BarChart3,
  'Simulateur de gains': BarChart3,
  'Salle des marchés': TrendingUp,
  'Bourse': TrendingUp,
  'Crypto': Coins,
  'Acheter des cryptos': Coins,
  'Mes Cryptos': Coins,
  'Mon wallet': Wallet,
  'Mon Espace': User,
  'Historique': FileText,
  'Messagerie': MessageSquare,
  'Mes documents': FileText,
  'Profil': User,
  'Actualités': Newspaper,
  'Les épargnes': Shield,
  'Mes Actions': TrendingUp,
  'Mon portefeuille': Wallet,
  'Prévisualisation': BarChart3,
};

function getIcon(label) {
  return ICON_MAP[label] || LayoutDashboard;
}

function buildNavItems(ct) {
  const v = (key) => ct[key] !== false;
  const items = [];
  const accueilChildren = [];
  if (v('menu_dashboard')) accueilChildren.push({ label: 'Tableau de bord', href: '/client/dashboard' });
  if (v('menu_news')) accueilChildren.push({ label: 'Actualités', href: '/client/news' });
  if (accueilChildren.length === 1) items.push(accueilChildren[0]);
  else if (accueilChildren.length > 1) items.push({ label: 'Accueil', children: accueilChildren });
  const investChildren = [];
  if (v('menu_investments')) investChildren.push({ label: 'Placement', href: '/client/placements' });
  if (v('menu_my_investments')) investChildren.push({ label: 'Mes Investissements', href: '/client/my-investments' });
  investChildren.push({ label: 'Comparer', href: '/client/compare' });
  if (investChildren.length === 1) items.push(investChildren[0]);
  else if (investChildren.length > 1) items.push({ label: 'Investissements', children: investChildren });
  const simChildren = [];
  if (v('menu_simulator')) simChildren.push({ label: 'Simulateur de gains', href: '/client/simulator' });
  if (v('menu_simulation')) simChildren.push({ label: 'Prévisualisation', href: '/client/products' });
  if (simChildren.length === 1) items.push(simChildren[0]);
  else if (simChildren.length > 1) items.push({ label: 'Simulation', children: simChildren });
  if (v('menu_trade')) items.push({ label: 'Salle des marchés', href: '/client/arbitrage' });
  const bourseChildren = [];
  if (v('menu_stock')) {
    if (v('submenu_bourse_salle')) bourseChildren.push({ label: 'Salle des marchés', href: '/client/bourse' });
    if (v('submenu_bourse_epargnes')) bourseChildren.push({ label: 'Les épargnes', href: '/client/portfolios' });
    if (v('submenu_bourse_portefeuille')) bourseChildren.push({ label: 'Mon portefeuille', href: '/client/portfolios' });
    if (v('submenu_bourse_actions')) bourseChildren.push({ label: 'Mes Actions', href: '/client/stock-portfolio' });
  }
  if (bourseChildren.length === 1) items.push(bourseChildren[0]);
  else if (bourseChildren.length > 1) items.push({ label: 'Bourse', children: bourseChildren });
  const cryptoChildren = [];
  if (v('menu_crypto')) {
    if (v('submenu_crypto_salle')) cryptoChildren.push({ label: 'Acheter des cryptos', href: '/client/crypto-buy' });
    if (v('submenu_crypto_portfolio') || !('submenu_crypto_portfolio' in ct)) cryptoChildren.push({ label: 'Mes Cryptos', href: '/client/crypto-portfolio' });
    if (v('submenu_crypto_epargnes')) cryptoChildren.push({ label: 'Les épargnes', href: '/client/portfolios' });
  }
  if (v('menu_wallets')) cryptoChildren.push({ label: 'Mon wallet', href: '/client/wallets' });
  if (cryptoChildren.length === 1) items.push(cryptoChildren[0]);
  else if (cryptoChildren.length > 1) items.push({ label: 'Crypto', children: cryptoChildren });
  const espaceChildren = [];
  if (v('menu_transactions')) espaceChildren.push({ label: 'Historique', href: '/client/transactions' });
  if (v('menu_messages')) espaceChildren.push({ label: 'Messagerie', href: '/messages' });
  if (v('menu_documents')) espaceChildren.push({ label: 'Mes documents', href: '/client/documents' });
  if (v('menu_profile')) espaceChildren.push({ label: 'Profil', href: '/client/profile' });
  if (espaceChildren.length === 1) items.push(espaceChildren[0]);
  else if (espaceChildren.length > 1) items.push({ label: 'Mon Espace', children: espaceChildren });
  return items;
}

/* ─── Dropdown with icon ─── */
function NavDropdown({ item, primary, fg, mutedFg, cardBg, borderColor }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const isChildActive = item.children?.some(c => location.pathname === c.href);
  const Icon = getIcon(item.label);

  return (
    <div className="relative" onMouseEnter={() => { clearTimeout(timeoutRef.current); setOpen(true); }} onMouseLeave={() => { timeoutRef.current = setTimeout(() => setOpen(false), 150); }}>
      <button
        className="flex flex-col items-center justify-center h-full px-3 text-[12px] font-medium transition-colors duration-150"
        style={{ color: isChildActive ? primary : fg }}
      >
        <div className="flex items-center gap-1.5">
          <Icon className="w-4 h-4" strokeWidth={1.5} style={{ opacity: isChildActive ? 1 : 0.6 }} />
          <span className="hidden xl:inline">{item.label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div
        className="absolute top-full left-1/2 -translate-x-1/2 pt-1 z-50"
        style={{ opacity: open ? 1 : 0, visibility: open ? 'visible' : 'hidden', transition: 'opacity 150ms', pointerEvents: open ? 'auto' : 'none' }}
      >
        <div className="py-1.5 rounded-xl shadow-2xl min-w-[210px] border backdrop-blur-xl"
          style={{ background: cardBg + 'F0', borderColor }}>
          {item.children.map(child => {
            const active = location.pathname === child.href;
            const ChildIcon = getIcon(child.label);
            return (
              <Link key={child.href} to={child.href}
                className="flex items-center gap-2.5 px-4 py-2.5 text-[12px] transition-colors duration-100"
                style={{ color: active ? primary : fg, fontWeight: active ? 600 : 400, background: active ? primary + '10' : 'transparent' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = `${borderColor}60`; }}
                onMouseLeave={e => { e.currentTarget.style.background = active ? primary + '10' : 'transparent'; }}
              >
                <ChildIcon className="w-3.5 h-3.5" strokeWidth={1.5} style={{ opacity: 0.6 }} />
                {child.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Nav item with icon ─── */
function NavItem({ item, primary, fg }) {
  const location = useLocation();
  const isActive = location.pathname === item.href || (item.href === '/client/dashboard' && location.pathname === '/client');
  const Icon = getIcon(item.label);

  return (
    <Link to={item.href}
      className="flex items-center gap-1.5 h-full px-3 text-[12px] font-medium transition-all duration-150"
      style={{
        color: isActive ? primary : fg,
        borderBottom: isActive ? `2px solid ${primary}` : '2px solid transparent',
      }}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} style={{ opacity: isActive ? 1 : 0.6 }} />
      <span className="hidden xl:inline">{item.label}</span>
    </Link>
  );
}

/* ─── User menu ─── */
function UserMenu({ user, primary, fg, mutedFg, cardBg, borderColor, colors, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const initials = user ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() : '?';

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const t = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', handler); };
  }, [open]);

  const go = (href) => { setOpen(false); setTimeout(() => navigate(href), 10); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 py-1 px-2 rounded-full transition-colors"
        style={{ background: open ? `${borderColor}60` : 'transparent' }} data-testid="v4-user-menu">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold"
          style={{ background: primary + '18', color: primary, border: `1.5px solid ${primary}30` }}>
          {initials}
        </div>
        <span className="text-[12px] font-medium hidden sm:inline">{user?.first_name}</span>
        <ChevronDown className="w-3 h-3" style={{ color: mutedFg, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 py-1 rounded-xl shadow-2xl min-w-[240px] border z-[60] backdrop-blur-xl"
          style={{ background: cardBg + 'F5', borderColor }} onClick={e => e.stopPropagation()}>
          <div className="px-4 py-3 border-b" style={{ borderColor: `${borderColor}80` }}>
            <p className="text-[13px] font-semibold">{user?.first_name} {user?.last_name}</p>
            <p className="text-[11px] mt-0.5" style={{ color: mutedFg }}>{user?.email}</p>
          </div>
          <div className="py-1 border-b" style={{ borderColor: `${borderColor}80` }}>
            {[
              { label: 'Ajout de fonds', icon: CreditCard, color: colors.success || '#4ADE80', href: '/client/transactions?action=deposit' },
              { label: 'Retrait', icon: Banknote, color: colors.warning || '#FBBF24', href: '/client/transactions?action=withdraw' },
              { label: 'Transfert interne', icon: ArrowRightLeft, color: colors.accent || '#60A5FA', href: '/client/dashboard?action=transfer' },
            ].map(m => (
              <button key={m.label} onMouseDown={() => go(m.href)}
                className="flex items-center gap-3 px-4 py-2.5 text-[12px] w-full text-left transition-colors cursor-pointer"
                style={{ color: m.color }}
                onMouseEnter={e => e.currentTarget.style.background = `${borderColor}60`}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <m.icon className="w-4 h-4" strokeWidth={1.5} /> {m.label}
              </button>
            ))}
          </div>
          <div className="py-1 border-b" style={{ borderColor: `${borderColor}80` }}>
            {[
              { label: 'Mon profil', icon: User, href: '/client/profile' },
              { label: 'Mes documents', icon: FileText, href: '/client/documents' },
            ].map(m => (
              <button key={m.label} onMouseDown={() => go(m.href)}
                className="flex items-center gap-3 px-4 py-2.5 text-[12px] w-full text-left transition-colors cursor-pointer"
                style={{ color: fg }}
                onMouseEnter={e => e.currentTarget.style.background = `${borderColor}60`}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <m.icon className="w-4 h-4" strokeWidth={1.5} /> {m.label}
              </button>
            ))}
          </div>
          <div className="py-1">
            <button onMouseDown={() => { setOpen(false); setTimeout(logout, 10); }}
              className="flex items-center gap-3 px-4 py-2.5 text-[12px] w-full text-left transition-colors cursor-pointer"
              style={{ color: colors.error || '#EF4444' }}
              onMouseEnter={e => e.currentTarget.style.background = `${borderColor}60`}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardLayoutV4({ children }) {
  const { user, logout, mirrorMode } = useAuth();
  const { platformSettings, clientTemplate } = useSettings();
  const { branding } = useTheme();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const token = localStorage.getItem('token');

  const NAV_ITEMS = useMemo(() => buildNavItems(clientTemplate || {}), [clientTemplate]);

  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/messages/unread-count`, { headers: h }).then(r => setUnread(r.data?.count || 0)).catch(() => {});
    axios.get(`${API}/notifications/unread-count`, { headers: h }).then(r => setNotifCount(r.data?.count || 0)).catch(() => {});
  }, [token]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const platformName = platformSettings?.platform_name || branding?.platformName || 'Plateforme';
  const logoUrl = branding?.logoUrl || platformSettings?.logo_url;
  const logoSize = branding?.logoSize || platformSettings?.logo_size || 40;
  const showName = branding?.showPlatformName !== false && platformSettings?.show_platform_name !== false;
  const headerHeight = Math.max(52, logoSize + 18);
  const primary = colors.primary || '#FFFFFF';
  const fg = colors.foreground || '#FAFAFA';
  const mutedFg = colors.mutedForeground || '#8A8A8A';
  const cardBg = colors.card || '#111111';
  const borderColor = colors.border || '#222222';
  const bg = colors.background || '#030303';

  return (
    <div className="min-h-screen" style={{ background: bg, color: fg }}>
      {mirrorMode && (
        <div className="text-center py-1 px-4 text-xs font-medium z-[60] relative" style={{ background: colors.warning || '#FBBF24', color: '#000' }} data-testid="mirror-mode-banner">
          Mode miroir — Vous visualisez l'espace de {user?.first_name} {user?.last_name}
          <button onClick={() => window.location.href = '/admin/clients'} className="ml-3 underline font-bold">Quitter</button>
        </div>
      )}

      {/* ═══ TOP BAR ═══ */}
      <header className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: bg + 'E8', borderBottom: `1px solid ${borderColor}` }}>
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 flex items-center justify-between" style={{ height: `${headerHeight}px` }}>

          {/* LEFT: Logo + Nav */}
          <div className="flex items-center gap-4 h-full">
            <Link to="/client/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="v4-logo">
              {logoUrl ? (
                <img src={logoUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : logoUrl}
                  alt="" style={{ height: `${logoSize}px`, width: 'auto' }} className="object-contain" />
              ) : (
                <div className="rounded-lg flex items-center justify-center font-bold"
                  style={{ width: `${logoSize}px`, height: `${logoSize}px`, fontSize: `${Math.max(10, logoSize / 3)}px`, background: primary, color: colors.primaryForeground || '#000' }}>
                  {platformName[0]}
                </div>
              )}
              {showName && <span className="text-[14px] font-bold hidden sm:inline tracking-tight">{platformName}</span>}
            </Link>

            <div className="w-px h-6 hidden lg:block" style={{ background: borderColor }} />

            {/* DESKTOP NAV */}
            <nav className="hidden lg:flex items-center h-full" data-testid="v4-nav-desktop">
              {NAV_ITEMS.map(item =>
                item.children
                  ? <NavDropdown key={item.label} item={item} primary={primary} fg={fg} mutedFg={mutedFg} cardBg={cardBg} borderColor={borderColor} />
                  : <NavItem key={item.label} item={item} primary={primary} fg={fg} />
              )}
            </nav>
          </div>

          {/* RIGHT: Actions */}
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/notifications')}
              className="relative p-2 rounded-lg transition-colors" style={{ ':hover': { background: `${borderColor}40` } }}
              onMouseEnter={e => e.currentTarget.style.background = `${borderColor}40`}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              data-testid="v4-notif-btn">
              <Bell className="w-[18px] h-[18px]" style={{ color: mutedFg }} strokeWidth={1.8} />
              {notifCount > 0 && (
                <span className="absolute top-1 right-1 w-[14px] h-[14px] rounded-full text-[8px] font-bold flex items-center justify-center"
                  style={{ background: colors.error || '#EF4444', color: '#fff' }}>{notifCount > 9 ? '9+' : notifCount}</span>
              )}
            </button>

            <button onClick={() => navigate('/messages')}
              className="relative p-2 rounded-lg transition-colors"
              onMouseEnter={e => e.currentTarget.style.background = `${borderColor}40`}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              data-testid="v4-msg-btn">
              <MessageSquare className="w-[18px] h-[18px]" style={{ color: mutedFg }} strokeWidth={1.8} />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-[14px] h-[14px] rounded-full text-[8px] font-bold flex items-center justify-center"
                  style={{ background: primary, color: colors.primaryForeground || '#000' }}>{unread > 9 ? '9+' : unread}</span>
              )}
            </button>

            <div className="w-px h-5 mx-1.5 hidden sm:block" style={{ background: borderColor }} />

            <UserMenu user={user} primary={primary} fg={fg} mutedFg={mutedFg} cardBg={cardBg} borderColor={borderColor} colors={colors} logout={logout} />

            <button className="lg:hidden p-2 rounded-lg ml-1" style={{ color: mutedFg }}
              onClick={() => setMobileOpen(!mobileOpen)} data-testid="v4-mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE NAV */}
        {mobileOpen && (
          <div className="lg:hidden border-t backdrop-blur-xl" style={{ borderColor, background: bg + 'F5' }}>
            <div className="max-w-[1600px] mx-auto px-4 py-3 space-y-0.5">
              {NAV_ITEMS.map(item => {
                const Icon = getIcon(item.label);
                return item.children ? (
                  <div key={item.label} className="py-1">
                    <p className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: mutedFg }}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {item.label}
                    </p>
                    {item.children.map(child => {
                      const ChildIcon = getIcon(child.label);
                      return (
                        <Link key={child.href} to={child.href}
                          className="flex items-center gap-2 px-6 py-2 text-[13px] rounded-lg transition-colors"
                          style={{ color: location.pathname === child.href ? primary : fg }}>
                          <ChildIcon className="w-3.5 h-3.5" strokeWidth={1.5} style={{ opacity: 0.5 }} /> {child.label}
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <Link key={item.href} to={item.href}
                    className="flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg transition-colors"
                    style={{ color: location.pathname === item.href ? primary : fg, fontWeight: location.pathname === item.href ? 600 : 400 }}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} style={{ opacity: 0.6 }} /> {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* ═══ CONTENT ═══ */}
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
